import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RootState } from '@/store';
import { adminApi } from '@/services/api';
import Button from '@/components/ui/Button';
import BackButton from '@/components/ui/BackButton';
import { CogIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const BusinessSettingsPage: React.FC = () => {
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [settings, setSettings] = useState({
    commission_rate: 5,
    delivery_charge: 50,
    minimum_commission: 10,
    maximum_commission: 100,
    currency: 'USD',
    platform_fee_percentage: 2.5,
  });

  // Get current role from URL
  const urlParams = new URLSearchParams(location.search);
  const currentRole = urlParams.get('role') || 'super_admin';

  useEffect(() => {
    fetchBusinessSettings();
  }, []);

  const fetchBusinessSettings = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await adminApi.getBusinessSettings();
      if (response.data?.settings) {
        setSettings(response.data.settings);
      }
    } catch (err: any) {
      console.error('Failed to fetch business settings:', err);
      // Use defaults if fetch fails
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setIsSaving(true);
      setError(null);
      
      await adminApi.saveBusinessSettings({
        commission_rate: settings.commission_rate,
        delivery_charge: settings.delivery_charge,
        minimum_commission: settings.minimum_commission,
        maximum_commission: settings.maximum_commission,
        currency: settings.currency,
        platform_fee_percentage: settings.platform_fee_percentage,
      });
      
      toast.success('Business settings saved successfully');
    } catch (err: any) {
      let errorMessage = 'Failed to save settings';
      if (err?.detail) {
        errorMessage = typeof err.detail === 'string' ? err.detail : String(err.detail);
      }
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field: string, value: string | number) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading business settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <div className="mb-6">
          <BackButton to={`/dashboard?role=${currentRole}`} />
        </div>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center">
            <CogIcon className="h-8 w-8 text-gray-500 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Business Settings</h1>
              <p className="text-gray-600 mt-2">Configure platform-wide business parameters</p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-6">
            {error}
          </div>
        )}

        {/* Settings Form */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">General Business Settings</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Commission Rate (%)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={settings.commission_rate}
                onChange={(e) => handleChange('commission_rate', parseFloat(e.target.value) || 0)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">Default commission percentage for all transactions</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Delivery Charge ($)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={settings.delivery_charge}
                onChange={(e) => handleChange('delivery_charge', parseFloat(e.target.value) || 0)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">Standard delivery charge amount</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Commission ($)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={settings.minimum_commission}
                onChange={(e) => handleChange('minimum_commission', parseFloat(e.target.value) || 0)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">Minimum commission amount per transaction</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Commission ($)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={settings.maximum_commission}
                onChange={(e) => handleChange('maximum_commission', parseFloat(e.target.value) || 0)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">Maximum commission amount per transaction</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Currency
              </label>
              <select
                value={settings.currency}
                onChange={(e) => handleChange('currency', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
                <option value="BDT">BDT (৳)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Platform Fee Percentage (%)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={settings.platform_fee_percentage}
                onChange={(e) => handleChange('platform_fee_percentage', parseFloat(e.target.value) || 0)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">Platform service fee percentage</p>
            </div>
          </div>

          <div className="mt-8 flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => window.history.back()}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveSettings}
              disabled={isSaving}
              className="px-8 py-3"
            >
              {isSaving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessSettingsPage;

